import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { adminFetch } from "../../lib/adminApi";
import { toast } from "sonner";
import { Save, RefreshCw } from "lucide-react";
import { AdminHomeTable } from "./AdminHomeTable";
import { professionalTimeline, academicTimeline, communityTimeline } from "../../lib/config";

interface AdminHomeTabProps {
  initialSettings: any[];
  timeline: any[];
  onRefresh: () => void;
}

function AdminHomeHeader({ saving }: { saving: boolean }) {
  return (
    <div className="flex justify-between items-center pb-2 border-b border-border/80">
      <div>
        <h4 className="text-xl font-bold text-foreground">Home Page Administration</h4>
        <p className="text-xs text-muted-foreground mt-0.5">Edit branding, bio and highlights sections</p>
      </div>
      <Button type="submit" disabled={saving} className="bg-brand-primary text-white">
        {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
        Save Changes
      </Button>
    </div>
  );
}

function resolveField(latest: any, field: "company" | "role" | "duration", lang: "en" | "tr"): string {
  if (field === "duration") {
    return latest.duration_en !== undefined ? (latest[`duration_${lang}`] || latest.duration || "") : (latest.duration || "");
  }

  const val = latest[field];
  if (val && typeof val === "object") {
    return val[lang] || "";
  }
  return latest[`${field}_${lang}`] || val || "";
}

function getTimelineEventVal(
  timeline: any[],
  type: "professional" | "academic" | "community",
  lang: "en" | "tr",
  field: "company" | "role" | "duration"
): string {
  const dbEvents = timeline.filter((e) => e.type === type);
  const sorted = dbEvents.length > 0
    ? [...dbEvents].sort((a, b) => (b.year || "").localeCompare(a.year || ""))
    : (type === "professional" ? professionalTimeline : type === "academic" ? academicTimeline : communityTimeline);

  const latest = sorted[0];
  if (!latest) return "";

  return resolveField(latest, field, lang);
}

function getHighlightVal(settings: any[], key: string, fallback = ""): string {
  const configSetting = settings.find((s) => s.key === "home_highlights_config")?.value;
  if (!configSetting) return fallback;
  try {
    const parsed = JSON.parse(configSetting);
    return parsed[key] || fallback;
  } catch {
    return fallback;
  }
}

function getVal(settings: any[], key: string, fallback = ""): string {
  const found = settings.find((s) => s.key === key);
  return found && found.value !== undefined && found.value !== null && found.value !== "" ? found.value : fallback;
}

function buildFormState(settings: any[], timeline: any[]) {
  const job_c_en_def = `${getTimelineEventVal(timeline, "professional", "en", "role")} @ ${getTimelineEventVal(timeline, "professional", "en", "company")}`;
  const job_c_tr_def = `${getTimelineEventVal(timeline, "professional", "tr", "role")} @ ${getTimelineEventVal(timeline, "professional", "tr", "company")}`;
  const job_d_en_def = getTimelineEventVal(timeline, "professional", "en", "duration");
  const job_d_tr_def = getTimelineEventVal(timeline, "professional", "tr", "duration");

  const acad_c_en_def = getTimelineEventVal(timeline, "academic", "en", "role");
  const acad_c_tr_def = getTimelineEventVal(timeline, "academic", "tr", "role");
  const acad_d_en_def = getTimelineEventVal(timeline, "academic", "en", "duration");
  const acad_d_tr_def = getTimelineEventVal(timeline, "academic", "tr", "duration");

  const hobby_c_en_def = "Books, Music & Films";
  const hobby_c_tr_def = "Kitaplar, Müzik ve Filmler";
  const hobby_d_en_def = "Curated personal favorites";
  const hobby_d_tr_def = "Kişisel favorilerim ve hobilerim";

  const comm_c_en_def = getTimelineEventVal(timeline, "community", "en", "company");
  const comm_c_tr_def = getTimelineEventVal(timeline, "community", "tr", "company");
  const comm_d_en_def = getTimelineEventVal(timeline, "community", "en", "role");
  const comm_d_tr_def = getTimelineEventVal(timeline, "community", "tr", "role");

  return {
    punchline_en: getVal(settings, "home_punchline_en", "Helping You Leverage AI in Your Work"),
    punchline_tr: getVal(settings, "home_punchline_tr", "Çalışmalarınızda Yapay Zekadan Yararlanmanıza Yardımcı Oluyorum"),
    bio_en: getVal(settings, "home_bio_en", "Hello, I am Uğurcan Uzunkaya. I bridge the gap between artificial intelligence and practical software engineering. Currently, I serve as Head of Technology at the Turkish AI Community and run projects across venture modeling and customized prints. I focus on building resilient systems that solve actual human needs, keeping complexity to a minimum and usability to the maximum."),
    bio_tr: getVal(settings, "home_bio_tr", "Merhaba, ben Uğurcan Uzunkaya. Yapay zeka ile pratik yazılım mühendisliği arasındaki boşluğu dolduruyorum. Şu anda Türkiye Yapay Zeka Topluluğu'nda Teknoloji Lideri olarak görev yapıyor, girişimcilik ve özel baskı sistemleri üzerine projeler yürütüyorum. Karmaşıklığı en aza, kullanışlılığı en üst düzeye indirerek gerçek insan ihtiyaçlarını çözen esnek sistemler inşa etmeye odaklanıyorum."),
    job_c_en: getHighlightVal(settings, "job_content_en", job_c_en_def),
    job_c_tr: getHighlightVal(settings, "job_content_tr", job_c_tr_def),
    job_d_en: getHighlightVal(settings, "job_desc_en", job_d_en_def),
    job_d_tr: getHighlightVal(settings, "job_desc_tr", job_d_tr_def),
    acad_c_en: getHighlightVal(settings, "acad_content_en", acad_c_en_def),
    acad_c_tr: getHighlightVal(settings, "acad_content_tr", acad_c_tr_def),
    acad_d_en: getHighlightVal(settings, "acad_desc_en", acad_d_en_def),
    acad_d_tr: getHighlightVal(settings, "acad_desc_tr", acad_d_tr_def),
    hobby_c_en: getHighlightVal(settings, "hobby_content_en", hobby_c_en_def),
    hobby_c_tr: getHighlightVal(settings, "hobby_content_tr", hobby_c_tr_def),
    hobby_d_en: getHighlightVal(settings, "hobby_desc_en", hobby_d_en_def),
    hobby_d_tr: getHighlightVal(settings, "hobby_desc_tr", hobby_d_tr_def),
    comm_c_en: getHighlightVal(settings, "comm_content_en", comm_c_en_def),
    comm_c_tr: getHighlightVal(settings, "comm_content_tr", comm_c_tr_def),
    comm_d_en: getHighlightVal(settings, "comm_desc_en", comm_d_en_def),
    comm_d_tr: getHighlightVal(settings, "comm_desc_tr", comm_d_tr_def),
    accepting_contracts: getVal(settings, "home_accepting_contracts", "true") === "true",
    timezone_offset: getVal(settings, "home_timezone_offset", "3"),
    profile_image: getVal(settings, "home_profile_image", ""),
  };
}

export function AdminHomeTab({ initialSettings, timeline = [], onRefresh }: AdminHomeTabProps) {
  const [form, setForm] = useState(() => buildFormState(initialSettings, timeline));

  useEffect(() => {
    if (initialSettings && initialSettings.length > 0) {
      setForm(buildFormState(initialSettings, timeline));
    }
  }, [initialSettings, timeline]);

  const [saving, setSaving] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const value = e.target.type === "checkbox" ? (e.target as HTMLInputElement).checked : e.target.value;
    setForm((prev) => ({ ...prev, [e.target.name]: value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminFetch("/api/admin/home", {
        punchline_en: form.punchline_en,
        punchline_tr: form.punchline_tr,
        bio_en: form.bio_en,
        bio_tr: form.bio_tr,
        accepting_contracts: form.accepting_contracts,
        timezone_offset: form.timezone_offset,
        profile_image: form.profile_image,
        highlights_config: {
          job_content_en: form.job_c_en,
          job_content_tr: form.job_c_tr,
          job_desc_en: form.job_d_en,
          job_desc_tr: form.job_d_tr,
          acad_content_en: form.acad_c_en,
          acad_content_tr: form.acad_c_tr,
          acad_desc_en: form.acad_d_en,
          acad_desc_tr: form.acad_d_tr,
          hobby_content_en: form.hobby_c_en,
          hobby_content_tr: form.hobby_c_tr,
          hobby_desc_en: form.hobby_d_en,
          hobby_desc_tr: form.hobby_d_tr,
          comm_content_en: form.comm_c_en,
          comm_content_tr: form.comm_c_tr,
          comm_desc_en: form.comm_d_en,
          comm_desc_tr: form.comm_d_tr,
        }
      });
      toast.success("Home page content updated successfully!");
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="space-y-6 max-w-3xl">
      <AdminHomeHeader saving={saving} />
      <AdminHomeTable form={form} handleChange={handleChange} />
    </form>
  );
}

export default AdminHomeTab;
